/**
 * The line between the plan and the panel beside it (issue #153, SP22 to SP24): drag it, or use the
 * keyboard, to make the panel wider or narrower. 420 px by default (Saker V1.2, #286; was 340), 280 to 560 px, and never so wide
 * that the plan has less than 430 px. Remembered in this browser and fitted again when the window
 * changes. Desktop split only; a phone has the panel as a sheet and no divider.
 */
import { T } from '@/i18n';
import { type RefObject, useCallback, useEffect, useState } from 'react';

export const PANEL_DEFAULT = 420;
const MIN = 280;
const MAX = 560;
const BOARD_MIN = 430;
const GAP = 14;
const STEP = 16;
const KEY = 'timefly.vaktplan.panelWidth';

/** The widest the panel may be in a split this wide: 560, or less so the plan keeps 430 px. */
export const maxFor = (split: number) => Math.max(MIN, Math.min(MAX, split - GAP - BOARD_MIN));
export const clampWidth = (w: number, split: number) => Math.round(Math.min(maxFor(split), Math.max(MIN, w)));

const stored = () => {
  try {
    const v = Number(localStorage.getItem(KEY));
    return v >= MIN && v <= MAX ? v : PANEL_DEFAULT;
  } catch {
    return PANEL_DEFAULT;
  }
};
const store = (w: number) => {
  try {
    localStorage.setItem(KEY, String(w));
  } catch {
    /* a private window keeps it for this visit only */
  }
};

/** The panel's width for this split, kept within its bounds as the split changes size. */
export function usePanelWidth(split: RefObject<HTMLElement | null>) {
  const [wish, setWish] = useState(stored);
  const [room, setRoom] = useState(Number.POSITIVE_INFINITY);
  useEffect(() => {
    const el = split.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setRoom(el.clientWidth));
    ro.observe(el);
    setRoom(el.clientWidth);
    return () => ro.disconnect();
  }, [split]);
  const width = Number.isFinite(room) ? clampWidth(wish, room) : wish;
  const set = useCallback(
    (w: number) => {
      const v = Number.isFinite(room) ? clampWidth(w, room) : Math.min(MAX, Math.max(MIN, Math.round(w)));
      setWish(v);
      store(v);
    },
    [room],
  );
  return { width, max: Number.isFinite(room) ? maxFor(room) : MAX, min: MIN, set };
}

export function PanelDivider({
  width,
  min,
  max,
  onWidth,
}: {
  width: number;
  min: number;
  max: number;
  onWidth: (w: number) => void;
}) {
  const [drag, setDrag] = useState<{ x: number; w: number } | null>(null);
  return (
    <div
      className={`vdivider${drag ? ' on' : ''}`}
      // biome-ignore lint/a11y/useSemanticElements: an <hr> takes no focus or value; a focusable separator is the ARIA pattern for a splitter
      role="separator"
      aria-orientation="vertical"
      aria-label={T('Bredde på panelet')}
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuetext={T('{n} piksler', { n: width })}
      tabIndex={0}
      data-testid="vdivider"
      title={T('Dra for å endre bredden. Dobbeltklikk for standard.')}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        setDrag({ x: e.clientX, w: width });
      }}
      onPointerMove={(e) => {
        // the panel is on the right: moving the line left makes it wider
        if (drag) onWidth(drag.w + (drag.x - e.clientX));
      }}
      onPointerUp={() => setDrag(null)}
      onPointerCancel={() => setDrag(null)}
      onLostPointerCapture={() => setDrag(null)}
      onDoubleClick={() => onWidth(PANEL_DEFAULT)}
      onKeyDown={(e) => {
        const to =
          e.key === 'ArrowLeft'
            ? width + STEP
            : e.key === 'ArrowRight'
              ? width - STEP
              : e.key === 'Home'
                ? min
                : e.key === 'End'
                  ? max
                  : e.key === 'Enter' || e.key === ' '
                    ? PANEL_DEFAULT
                    : null;
        if (to == null) return;
        e.preventDefault();
        onWidth(to);
      }}
    >
      <span aria-hidden="true" />
    </div>
  );
}

/**
 * The name column's width in the week grid, dragged at its right edge like the panel's line (owner
 * 2026-10-27): 188 px by default, 150 to 320, remembered in this browser. 188 holds a name such as «Sara
 * Skolebrød» beside its count of issues; at 168 it was cut (#272 A-S05).
 */
export const NAME_DEFAULT = 188;
const NAME_KEY = 'timefly.vaktplan.nameWidth';
const nameClamp = (w: number) => Math.round(Math.min(320, Math.max(150, w)));
export function useNameWidth() {
  const [w, setW] = useState(() => {
    try {
      const v = Number(localStorage.getItem(NAME_KEY));
      return v ? nameClamp(v) : NAME_DEFAULT;
    } catch {
      return NAME_DEFAULT;
    }
  });
  const set = useCallback((v: number) => {
    const c = nameClamp(v);
    setW(c);
    try {
      localStorage.setItem(NAME_KEY, String(c));
    } catch {
      /* a private window keeps it for this visit only */
    }
  }, []);
  return { width: w, set };
}

export function NameDivider({ width, onWidth }: { width: number; onWidth: (w: number) => void }) {
  const [drag, setDrag] = useState<{ x: number; w: number } | null>(null);
  return (
    <div
      className={`vnamediv${drag ? ' on' : ''}`}
      role="separator"
      aria-orientation="vertical"
      aria-label={T('Bredde på navnekolonnen')}
      aria-valuenow={width}
      aria-valuemin={140}
      aria-valuemax={320}
      tabIndex={0}
      data-testid="vnamediv"
      title={T('Dra for å endre bredden. Dobbeltklikk for standard.')}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        setDrag({ x: e.clientX, w: width });
      }}
      // the column is on the left: moving the line right makes it wider
      onPointerMove={(e) => drag && onWidth(drag.w + (e.clientX - drag.x))}
      onPointerUp={() => setDrag(null)}
      onPointerCancel={() => setDrag(null)}
      onLostPointerCapture={() => setDrag(null)}
      onDoubleClick={() => onWidth(NAME_DEFAULT)}
      onKeyDown={(e) => {
        const to =
          e.key === 'ArrowRight'
            ? width + STEP
            : e.key === 'ArrowLeft'
              ? width - STEP
              : e.key === 'Enter'
                ? NAME_DEFAULT
                : null;
        if (to == null) return;
        e.preventDefault();
        onWidth(to);
      }}
    />
  );
}
